"use client";

import { LiquidFixedCircleButton } from "../liquid-fixed-circle-button";
import { LIQUID_PILL_CIRCLE_DIAMETER } from "../liquid-pill";
import { Plus, Zap } from "lucide-react";
import * as React from "react";

export const MAINTAINER_FAB_OFFSET = 24;
export const MAINTAINER_FAB_STACK_GAP = 12;
export const MAINTAINER_FAB_SIZE = LIQUID_PILL_CIRCLE_DIAMETER.xl;
export const MAINTAINER_FAB_CHAT_SIZE = LIQUID_PILL_CIRCLE_DIAMETER.lg;

/**
 * Ya no elevamos el stack para esquivar el chat. En páginas con FAB, el propio
 * stack publica CSS vars globales para que `ChatOverlay` se mueva a la
 * izquierda del CTA `+` y quede alineado en la misma fila.
 */
export const MAINTAINER_FAB_CHAT_CLEARANCE = 0;

export function getMaintainerFabCreateOffsetY(offset = MAINTAINER_FAB_OFFSET) {
  return offset + MAINTAINER_FAB_CHAT_CLEARANCE;
}

export function getMaintainerFabQuickieOffsetY(offset = MAINTAINER_FAB_OFFSET) {
  return (
    offset +
    MAINTAINER_FAB_CHAT_CLEARANCE +
    MAINTAINER_FAB_SIZE +
    MAINTAINER_FAB_STACK_GAP
  );
}

export interface MaintainerFabStackV2Props {
  createAriaLabel: string;
  onCreate: () => void;
  createIcon?: React.ReactNode;
  createVariant?: "indigo" | "violet" | "emerald" | "gold" | "sky" | "slate";
  quickieVisible?: boolean;
  quickieAriaLabel?: string;
  onQuickie?: () => void;
  quickieIcon?: React.ReactNode;
  quickieVariant?: "indigo" | "violet" | "emerald" | "gold" | "sky" | "slate";
  offsetX?: number;
}

export function MaintainerFabStackV2({
  createAriaLabel,
  onCreate,
  createIcon = <Plus className="h-6 w-6" strokeWidth={1.75} />,
  createVariant = "indigo",
  quickieVisible = false,
  quickieAriaLabel,
  onQuickie,
  quickieIcon = <Zap className="h-6 w-6" strokeWidth={1.75} />,
  quickieVariant = "gold",
  offsetX = MAINTAINER_FAB_OFFSET,
}: MaintainerFabStackV2Props) {
  const createOffsetY = getMaintainerFabCreateOffsetY();
  const quickieOffsetY = getMaintainerFabQuickieOffsetY();

  React.useEffect(() => {
    if (typeof document === "undefined") return;

    const root = document.documentElement;
    const chatOffsetX = offsetX + MAINTAINER_FAB_SIZE + MAINTAINER_FAB_STACK_GAP;
    const chatOffsetY =
      createOffsetY + (MAINTAINER_FAB_SIZE - MAINTAINER_FAB_CHAT_SIZE) / 2;

    root.style.setProperty("--chat-offset-x", `${chatOffsetX}px`);
    root.style.setProperty("--chat-offset-y", `${chatOffsetY}px`);
    root.style.setProperty("--chat-offset-x-sm", `${chatOffsetX}px`);
    root.style.setProperty("--chat-offset-y-sm", `${chatOffsetY}px`);

    return () => {
      root.style.removeProperty("--chat-offset-x");
      root.style.removeProperty("--chat-offset-y");
      root.style.removeProperty("--chat-offset-x-sm");
      root.style.removeProperty("--chat-offset-y-sm");
    };
  }, [createOffsetY, offsetX]);

  return (
    <>
      <LiquidFixedCircleButton
        icon={createIcon}
        variant={createVariant}
        size="xl"
        horizontal="right"
        vertical="bottom"
        offsetX={offsetX}
        offsetY={createOffsetY}
        reactive
        hoverBounce
        hoverDeform
        onClick={onCreate}
        aria-label={createAriaLabel}
      />

      {quickieVisible && onQuickie ? (
        <LiquidFixedCircleButton
          icon={quickieIcon}
          variant={quickieVariant}
          size="xl"
          horizontal="right"
          vertical="bottom"
          offsetX={offsetX}
          offsetY={quickieOffsetY}
          reactive
          hoverBounce
          hoverDeform
          onClick={onQuickie}
          aria-label={quickieAriaLabel}
        />
      ) : null}
    </>
  );
}
